import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Spinner, Field, TextArea } from "../components/ui";
import AppHeader from "../components/AppHeader";
import { CheckCircle2, Copy, Link as LinkIcon, RotateCw, Unlink, Upload, ExternalLink } from "lucide-react";

export default function Sheets() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [json, setJson] = useState("");
  const [title, setTitle] = useState("Studio Grape · CRM Data");
  const [shareWith, setShareWith] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try { setStatus(await api.get("/sheets/status")); }
    catch (e) { alert(e?.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (user?.role !== "admin") return <><AppHeader title="Sheets Sync" back /><div className="sg-main" style={{ color: "var(--muted)" }}>Admins only.</div></>;
  if (loading) return <><AppHeader title="Sheets Sync" back /><div className="sg-main"><Spinner /></div></>;

  const onConnect = async () => {
    if (!json.trim()) { alert("Paste Service Account JSON"); return; }
    let parsed;
    try { parsed = JSON.parse(json); }
    catch { alert("Invalid JSON"); return; }
    if (parsed.type !== "service_account") { alert("Not a service account key"); return; }
    setBusy("connect");
    try {
      const shareList = shareWith.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
      const r = await api.post("/sheets/connect", { service_account_json: parsed, spreadsheet_title: title || undefined, share_with: shareList });
      setStatus(r);
      setJson("");
      alert("Connected — Google Sheet created. Initial sync complete.");
    } catch (e) { alert(e?.message); } finally { setBusy(null); }
  };

  const onSync = async () => {
    setBusy("sync");
    try { await api.post("/sheets/sync", {}); alert("Synced"); await load(); }
    catch (e) { alert(e?.message); } finally { setBusy(null); }
  };

  const onBackupDocs = async () => {
    setBusy("backup");
    try { const r = await api.post("/sheets/backup-documents", {}); alert(`Backup: ${r.uploaded} uploaded, ${r.failed} failed`); await load(); }
    catch (e) { alert(e?.message); } finally { setBusy(null); }
  };

  const onDisconnect = async () => {
    if (!window.confirm("Disconnect Google Sheets?")) return;
    setBusy("disconnect");
    try { await api.delete("/sheets/disconnect"); setStatus({ connected: false }); }
    catch (e) { alert(e?.message); } finally { setBusy(null); }
  };

  const copySAEmail = async () => {
    await navigator.clipboard.writeText(status?.service_account_email || "");
    alert("Service account email copied");
  };

  return (
    <>
      <AppHeader title="Google Sheets Sync" back />
      <div className="sg-main sg-fade-in">
      {status?.connected ? (
        <>
          <div className="sg-card" style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <CheckCircle2 size={20} color="var(--success)" />
              <span style={{ fontSize: 16, fontWeight: 700 }}>Connected</span>
            </div>
            <K l="Spreadsheet" v={<a href={status.spreadsheet_url} target="_blank" rel="noreferrer" style={{ color: "var(--brand)", textDecoration: "underline" }}>{status.spreadsheet_url}</a>} />
            <K l="Service Account" v={
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <code style={{ fontSize: 12 }}>{status.service_account_email}</code>
                <button className="sg-btn" style={{ padding: 6 }} onClick={copySAEmail}><Copy size={13} /></button>
              </span>
            } />
            <K l="Shared with" v={(status.shared_with || []).join(", ") || "—"} />
            <K l="Last sync" v={status.last_sync_at ? new Date(status.last_sync_at).toLocaleString() : "—"} />
            {status.last_sync_error && <div style={{ color: "var(--danger)", fontSize: 12, marginTop: 6 }}>{status.last_sync_error}</div>}
          </div>

          <a className="sg-btn sg-btn-primary" href={status.spreadsheet_url} target="_blank" rel="noreferrer" style={{ width: "100%", marginBottom: 10 }}>
            <ExternalLink size={15} /> Open Google Sheet
          </a>
          <button className="sg-btn" onClick={onSync} disabled={busy === "sync"} style={{ width: "100%", marginBottom: 10 }}>
            <RotateCw size={15} /> {busy === "sync" ? "Syncing..." : "Sync everything now"}
          </button>

          <div className="sg-card" style={{ marginTop: 14 }}>
            <div className="sg-label" style={{ marginTop: 0 }}>Drive · Customer Documents</div>
            {status.drive_root_folder_url ? (
              <a href={status.drive_root_folder_url} target="_blank" rel="noreferrer" style={{ color: "var(--brand)", textDecoration: "underline", fontSize: 12 }}>{status.drive_root_folder_url}</a>
            ) : (
              <div style={{ color: "var(--muted)", fontSize: 13 }}>Not yet created — tap "Backup all documents" to create it.</div>
            )}
            {status.last_docs_backup_at && (
              <div style={{ fontSize: 12, marginTop: 8 }}>Last backup: {new Date(status.last_docs_backup_at).toLocaleString()}{status.last_docs_backup_summary ? ` · ${status.last_docs_backup_summary}` : ""}</div>
            )}
            <button className="sg-btn" onClick={onBackupDocs} disabled={busy === "backup"} style={{ marginTop: 12 }}>
              <Upload size={15} /> {busy === "backup" ? "Backing up..." : "Backup all documents now"}
            </button>
          </div>

          <button className="sg-btn sg-btn-danger" onClick={onDisconnect} disabled={busy === "disconnect"} style={{ width: "100%", marginTop: 18 }}>
            <Unlink size={15} /> Disconnect
          </button>
        </>
      ) : (
        <>
          <div className="sg-card" style={{ marginBottom: 14 }}>
            <h3 style={{ fontSize: 17, fontWeight: 800, margin: "0 0 6px" }}>Connect a Google Sheet</h3>
            <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.5 }}>
              Mirror all customers, meetings & quote summaries into a Google Sheet your team can view. One-way push only.
            </div>
          </div>
          <div className="sg-card" style={{ marginBottom: 14 }}>
            <Field label="Spreadsheet title" value={title} onChange={(e) => setTitle(e.target.value)} />
            <Field label="Share with (comma-separated)" value={shareWith} onChange={(e) => setShareWith(e.target.value)} placeholder="teammate@studiogrape.com, sampling@studiogrape.com" />
            <TextArea label="Service Account JSON" value={json} onChange={(e) => setJson(e.target.value)} placeholder='{ "type": "service_account", ... }' style={{ minHeight: 180, fontFamily: "monospace", fontSize: 12 }} />
            <button className="sg-btn sg-btn-primary" onClick={onConnect} disabled={busy === "connect"} style={{ width: "100%", marginTop: 10 }}>
              <LinkIcon size={15} /> {busy === "connect" ? "Creating sheet..." : "Connect & create sheet"}
            </button>
          </div>
          <div className="sg-card">
            <h4 style={{ fontSize: 14, fontWeight: 800, margin: "0 0 10px" }}>How to get the Service Account JSON</h4>
            {[
              "Go to console.cloud.google.com and create (or pick) a project.",
              "Enable both 'Google Sheets API' and 'Google Drive API'.",
              "Go to IAM & Admin → Service Accounts → Create Service Account.",
              "Click the new account → Keys → Add Key → Create new key → JSON.",
              "Open the .json file, copy all contents, paste above.",
              "The app will create a sheet, share it with you, and sync all data.",
            ].map((t, i) => (
              <div key={i} style={{ display: "flex", gap: 10, marginBottom: 8 }}>
                <div style={{ width: 22, height: 22, borderRadius: 11, background: "var(--brand)", color: "#fff", display: "grid", placeItems: "center", fontSize: 11, fontWeight: 800, flexShrink: 0 }}>{i + 1}</div>
                <div style={{ fontSize: 13, lineHeight: 1.4 }}>{t}</div>
              </div>
            ))}
          </div>
        </>
      )}
      </div>
    </>
  );
}

function K({ l, v }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div className="sg-label" style={{ marginTop: 0 }}>{l}</div>
      <div style={{ fontSize: 13 }}>{v}</div>
    </div>
  );
}
